import { getApiClient } from '@apps/web/lib/api_client';
import { type ApiResponseType } from '@libraries/typescript/api_client';

export type GenerationsRecurrenceSelectionPreview = ApiResponseType<
    '/organizations/{organization_id}/callouts/{callout_id}/generations-finite-recurrence-preview',
    'post'
>;

type LoadGenerationsRecurrenceSelectionArgs = {
    readonly organizationId: string;
    readonly outreachId: string;
    readonly caregiverId: string;
    readonly shiftIds: ReadonlyArray<string>;
};

export async function loadGenerationsRecurrenceSelection({
    organizationId,
    outreachId,
    caregiverId,
    shiftIds,
}: LoadGenerationsRecurrenceSelectionArgs): Promise<GenerationsRecurrenceSelectionPreview> {
    return getApiClient().post({
        url: '/organizations/{organization_id}/callouts/{callout_id}/generations-finite-recurrence-preview',
        pathParams: {
            organization_id: organizationId,
            callout_id: outreachId,
        },
        body: {
            shift_ids: [...shiftIds],
            caregiver_id: caregiverId,
        },
        reason: 'GenerationsFiniteRecurrencePreview',
    });
}

type GenerationsRecurrenceOccurrence =
    GenerationsRecurrenceSelectionPreview['computed_occurrences'][number];

function formatGenerationsOccurrence(
    occurrence: GenerationsRecurrenceOccurrence,
): string {
    const formatDate = (value: string) => {
        const [year, month, day] = value.split('-').map(Number);
        return new Intl.DateTimeFormat('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        }).format(new Date(year!, month! - 1, day!, 12));
    };
    const formatTime = (value: string) => {
        const [hour, minute] = value.split(':').map(Number);
        return new Intl.DateTimeFormat('en-US', {
            hour: 'numeric',
            minute: '2-digit',
        }).format(new Date(2000, 0, 1, hour!, minute!));
    };
    const end =
        occurrence.end_date === occurrence.start_date
            ? formatTime(occurrence.end_time)
            : `${formatDate(occurrence.end_date)} ${formatTime(occurrence.end_time)}`;
    return `${formatDate(occurrence.start_date)} ${formatTime(occurrence.start_time)}–${end}`;
}

export function GenerationsOccurrenceList({
    occurrences,
}: {
    readonly occurrences: ReadonlyArray<GenerationsRecurrenceOccurrence>;
}) {
    return (
        <ul className="list-disc pl-5 text-sm">
            {occurrences.map((occurrence) => (
                <li key={occurrence.external_shift_id}>
                    {formatGenerationsOccurrence(occurrence)}
                </li>
            ))}
        </ul>
    );
}
